/**
 * The curated glyph shortlist and the one cell that draws a glyph by name.
 *
 * Extracted from `MiniappIconPickers.tsx` for a compact creator that has since
 * been removed (Chu's third review put icon-making back in the full maker). The
 * split is kept because it is the right one either way: the LIST and the CELL
 * are data plus a leaf component, while `MiniappIconPickers` is the popover
 * shell around them — `FloatingPanel`, the emoji dataset, `react-colorful`. A
 * future surface that wants to offer a glyph can import these two without any
 * of that, which was the whole point and is still true.
 */
import React, { Suspense } from 'react'
import { PDAPP_GLYPH_NAME, PDAPP_GLYPH_PATH, glyphStrokeStyle } from '@papan/types'

// Lucide's render-by-name component. Lazy so the (large) icon manifest only
// loads when a glyph grid is actually shown.
const DynamicIconLazy = React.lazy(() =>
  import('lucide-react/dynamic').then((m) => ({ default: m.DynamicIcon })),
)

/** A curated shortlist shown before the user searches — so a picker is useful
 *  immediately without kicking off dozens of per-icon lazy chunk loads. */
export const CURATED_LUCIDE = [
  // The PDApp mark leads — it is the product's own symbol and the default tile's
  // glyph. Not a Lucide name (see PDAPP_GLYPH_NAME); `GlyphPreview` draws it
  // directly because lucide's DynamicIcon has no entry for it.
  PDAPP_GLYPH_NAME,
  'layout-grid', 'rocket', 'sparkles', 'star', 'heart', 'zap', 'flame', 'bolt',
  'box', 'package', 'boxes', 'blocks', 'component', 'shapes', 'palette', 'brush',
  'code', 'terminal', 'braces', 'binary', 'database', 'server', 'cloud', 'globe',
  'file-text', 'folder', 'book-open', 'notebook', 'clipboard-list', 'list-todo', 'calendar', 'clock',
  'message-square', 'mail', 'bell', 'users', 'user', 'contact', 'phone', 'video',
  'image', 'camera', 'music', 'headphones', 'mic', 'film', 'map', 'compass',
  'shopping-cart', 'shopping-bag', 'credit-card', 'wallet', 'dollar-sign', 'gift', 'tag', 'ticket',
  'settings', 'sliders-horizontal', 'wrench', 'hammer', 'bug', 'shield', 'lock', 'key',
  'chart-line', 'chart-bar', 'chart-pie', 'trending-up', 'activity', 'gauge', 'target', 'flag',
  'search', 'filter', 'bookmark', 'pin', 'link', 'paperclip', 'hash', 'at-sign',
  'sun', 'moon', 'cloud-rain', 'leaf', 'tree-pine', 'flower', 'bird', 'cat',
  'gamepad-2', 'dices', 'puzzle', 'trophy', 'medal', 'crown', 'graduation-cap', 'lightbulb',
]

/**
 * One grid cell's glyph. Routes the reserved PDApp mark to our own geometry —
 * lucide's DynamicIcon has no entry for it and would render nothing — and
 * carries the mark's flat caps, which its four separate segments depend on.
 *
 * Draws in `currentColor`, i.e. the SURFACE's foreground, and deliberately NOT
 * in the icon's chosen glyph colour: a grid picks a SHAPE, and painting the
 * cells in the chosen colour makes every one of them invisible whenever that
 * colour approaches the surface behind it (white glyphs — the default — vanish
 * on a light panel, dark ones on a dark panel).
 */
export function GlyphPreview({ name, size }: { name: string; size: number }) {
  if (name !== PDAPP_GLYPH_NAME) {
    return (
      <Suspense fallback={<span style={{ width: size, height: size, display: 'inline-block' }} />}>
        <DynamicIconLazy name={name as never} size={size} />
      </Suspense>
    )
  }
  const stroke = glyphStrokeStyle(name)
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap={stroke.cap}
      strokeLinejoin={stroke.join}
    >
      <path d={PDAPP_GLYPH_PATH} />
    </svg>
  )
}
